import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { PlatformLogo } from "@/components/PlatformLogo";
import { Home } from "lucide-react";

// Capture the hash BEFORE Supabase processes and clears it
// Falls back to sessionStorage set by RecoveryRedirect in App.tsx
const initialHash = typeof window !== "undefined" ? window.location.hash : "";
const startsAsRecovery =
  initialHash.includes("type=recovery") ||
  initialHash.includes("access_token") ||
  sessionStorage.getItem("dietitech_recovery") === "true";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Start timeout — if no recovery session in 6s, show error
    timeoutRef.current = setTimeout(() => {
      setTimedOut((prev) => {
        if (!sessionReady) return true;
        return prev;
      });
    }, 6000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" && session) {
        setSessionReady(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      } else if (event === "SIGNED_IN" && session && startsAsRecovery) {
        // Supabase sometimes fires SIGNED_IN instead of PASSWORD_RECOVERY
        setSessionReady(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      }
      // Do NOT navigate anywhere here — stay on reset page
    });

    // Also check existing session in case event already fired
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user && startsAsRecovery) {
        setSessionReady(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      }
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast({ title: "خطأ", description: "يرجى إدخال كلمة المرور وتأكيدها", variant: "destructive" });
      return;
    }
    if (password.length < 8) {
      toast({ title: "خطأ", description: "يجب أن تكون كلمة المرور 8 أحرف على الأقل", variant: "destructive" });
      return;
    }
    if (password !== confirmPassword) {
      toast({ title: "خطأ", description: "كلمة المرور وتأكيدها غير متطابقين", variant: "destructive" });
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
    } else {
      sessionStorage.removeItem("dietitech_recovery");
      toast({ title: "تم التحديث", description: "تم تحديث كلمة المرور بنجاح. يمكنك الآن تسجيل الدخول" });
      await supabase.auth.signOut();
      navigate("/auth");
    }
  };

  // No recovery hash at all — invalid page access
  if (!startsAsRecovery) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center space-y-4" dir="rtl">
            <p className="text-muted-foreground">الرابط غير صالح. يرجى طلب رابط إعادة تعيين جديد.</p>
            <Button className="w-full" onClick={() => navigate("/auth")}>العودة لتسجيل الدخول</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background flex items-center justify-center p-4 relative">
      <Button variant="outline" size="icon" onClick={() => navigate("/")} className="absolute top-4 right-4">
        <Home size={24} />
      </Button>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <PlatformLogo className="w-24 h-24 object-contain" alt="DietiTech Logo" />
          </div>
          <CardTitle className="text-2xl">إعادة تعيين كلمة المرور</CardTitle>
          <CardDescription>أدخل كلمة المرور الجديدة</CardDescription>
        </CardHeader>
        <CardContent>
          {timedOut && !sessionReady ? (
            <div className="text-center space-y-4" dir="rtl">
              <p className="text-destructive text-sm">
                انتهت صلاحية الرابط أو استُخدم مسبقاً. يرجى طلب رابط جديد.
              </p>
              <Button className="w-full" onClick={() => navigate("/auth")}>طلب رابط جديد</Button>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4" dir="rtl">
              {!sessionReady && (
                <p className="text-center text-sm text-muted-foreground animate-pulse">
                  جاري التحقق من الرابط...
                </p>
              )}
              <div className="space-y-2">
                <Label htmlFor="password">كلمة المرور الجديدة</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading || !sessionReady}
                  dir="ltr"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">تأكيد كلمة المرور</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading || !sessionReady}
                  dir="ltr"
                />
                <p className="text-xs text-muted-foreground">يجب أن تكون كلمة المرور 8 أحرف على الأقل</p>
              </div>
              <Button type="submit" className="w-full" disabled={loading || !sessionReady}>
                {loading ? "جاري التحديث..." : !sessionReady ? "جاري التحقق..." : "تحديث كلمة المرور"}
              </Button>
              <Button type="button" variant="link" className="w-full text-sm" onClick={() => navigate("/auth")}>
                العودة لتسجيل الدخول
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
